import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridPaginationModel,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridOverlay,
  GridToolbarQuickFilter,
  GridValueGetter,
  GridCloseIcon,
} from "@mui/x-data-grid";
import {
  Button,
  Typography,
  Box,
  Grid,
  styled,
  CircularProgress,
  Switch,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Divider,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import ToastSnackbar, { SnackbarHandle } from "../../common/ToastNotification";
import HomeIcon from "@mui/icons-material/Home";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import { ListFilteredStudents } from "../../api/Students-Management/add-view-students";
import { Get as GetClass } from "../../api/Control-Settings/manage-class";
import { GetSections } from "../../api/Exams-Management/manage-section";
import { useForm } from "react-hook-form";
import ControlledSelect from "../../common/ControlledComponents/ControlledSelect";
import ControlledTextField from "../../common/ControlledComponents/ControlledTextField";
import { useUser } from "../../store/UserLoginContext";
import { getCurrentUser } from "../../service/authService";
import { GetClassFeeStructureForClass } from "../../api/Control-Settings/manage-fee-heads";
import { UpdateStudentv2 } from "../../api/Students-Management/bulk-update";

const CustomNoRowsOverlay = ({ loading }: { loading: boolean }) => {
  return (
    <GridOverlay
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <Box sx={{ textAlign: "center", padding: 2 }}>
        {loading ? (
          <>
            <CircularProgress size={40} />
            <Typography variant="h6" color="textSecondary" mt={2}>
              Loading data...
            </Typography>
          </>
        ) : (
          <Typography variant="h5" color="textSecondary">
            NO DATA AVAILABLE
          </Typography>
        )}
      </Box>
    </GridOverlay>
  );
};

const givenByOptions = [
  "Allowed By Head Sir",
  "Allowed By Mama Sir",
  "Allowed By Sahay Sir",
  "Allowed By Principal",
  "Allowed By Munna Sir",
  "Allowed By Acd.Director",
  "Allowed By Pappu Sir",
];

// Create buttons with hover underline animation
const AnimatedButton = ({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) => {
  return (
    <Button
      variant="text"
      onClick={onClick}
      sx={{
        position: "relative",
        // fontFamily: "Motiva Sans Bold",
        // fontWeight: "Bold",
        padding: "0 10px",
        fontSize: "14px",
        textTransform: "none",
        color: "#2e186a",
        "&:hover": {
          fontWeight: "Bold",
        },
        "&::after": {
          content: '""',
          fontWeight: "Bold",
          position: "absolute",
          width: "0",
          height: "2px",
          left: "0",
          bottom: "-2px",
          backgroundColor: "rgb(255, 130, 92)",
          transition: "width 0.3s ease-in-out",
        },
        "&:hover::after": {
          width: "100%", // Underline expands on hover
        },
      }}
      disabled={disabled == null ? true : disabled}
    >
      <strong>{label}</strong>
    </Button>
  );
};

const MyCustomButton = styled(Button)(({ theme }) => ({
  fontFamily: "Motiva Sans Bold",
  fontSize: "0.80rem",
  fontWeight: "700",
  border: "1px solid #edf3f5",
  borderRadius: "4px",
  background: "#2e186a",
  boxShadow: "0 16px 30px rgb(23 31 114 / 20%)",
  marginTop: "0rem",
  "&:hover": {
    color: "#fff",
    border: "1px solid rgb(255, 130, 92)",
    backgroundColor: "rgb(255, 130, 92)",
  },
}));

const StudentScholarship = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<any>([]);

  const [isProfileDialogOpen, setProfileDialogOpen] = useState(false);
  const resetFormRef = useRef<() => void>(() => {});

  const snackbarRef = useRef<SnackbarHandle>(null);
  const [discountData, setDiscountData] = useState<any>({});

  const [classList, setClassList] = useState<any>([]);
  const [sectionList, setSectionList] = useState<any>([]);

  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [fetchingFeeStructure, setFetchingFeeStructure] = useState(false);

  const [loggedInUser, setLoggedInUser] = useState("");
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [updatingDiscount, setUpdatingDiscount] = useState(false);

  useEffect(() => {
    const { username } = getCurrentUser();
    setLoggedInUser(username);
  }, []);

  const {
    handleSubmit,
    reset,
    setError,
    formState: { errors },
    control,
  } = useForm({
    defaultValues: {
      admission_id: "",
      name: "",
      class_id: "",
      section_id: "",
      roll_number: "",
    },
    mode: "onTouched",
  });
  const onClose = () => {
    if (resetFormRef.current) {
      resetFormRef.current(); // Reset the form to its initial state
    }
    setProfileDialogOpen(false);
    setDiscountData({});
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [classes_records, section_records] = await Promise.all([
          GetClass(),
          GetSections(),
        ]);

        if (classes_records && classes_records.result.documents?.length > 0) {
          setClassList(classes_records.result.documents);
        }
        if (section_records && section_records.result.documents?.length > 0) {
          setSectionList(section_records.result.documents);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        snackbarRef.current?.showSnackbar(`Error fetching data`, "error");
      } finally {
        setLoading(false);
      }
    };
    console.log("******** USER FETCHED AS BELOW ************* ");
    console.log(user?.username);
    fetchData();
  }, []);

  const JSONParseStudentObject = (student_string: any) => {
    try {
      const student_json_parsed = student_string.map((stud: any) => ({
        ...stud,
        transport_details: JSON.parse(stud.transport_details),
        personal_details: JSON.parse(stud.personal_details),
        guardian_details: JSON.parse(stud.guardian_details),
        father_details: JSON.parse(stud.father_details),
        mother_details: JSON.parse(stud.mother_details),
        previous_school: JSON.parse(stud.previous_school),
      }));

      return student_json_parsed;
    } catch (error) {
      console.log("Error JSON parsing student object");
      console.log(error);
      return [];
    }
  };

  const handleViewClick = async (rowData: any) => {
    try {
      setFetchingFeeStructure(true);

      console.log("View Clicked", rowData.student_id);
      setProfileDialogOpen(true);
      let _discounts_json = {};
      const _discounts = rowData?.discount || null;
      if (_discounts && _discounts !== "0") {
        console.log("Non Zero Discount.");
        _discounts_json = JSON.parse(_discounts);
        setDiscountData(_discounts_json);
        setSelectedStudent(rowData);
      } else {
        console.log("Discount Present Already");
        const payload = {
          class_name: rowData.class_name,
        };
        const fee_structure = await GetClassFeeStructureForClass(payload);

        const fee_structure_class = fee_structure.result?.documents[0] || {};

        if (
          fee_structure_class &&
          Object.entries(fee_structure_class).length > 0
        ) {
          const parsedFees = JSON.parse(fee_structure_class.monthly_fees);

          var _fee_particulars = parsedFees.map(
            (record: any) => record.fees_particulars
          );

          const result = {};

          //@ts-ignore
          _fee_particulars.forEach((obj) => {
            Object.entries(obj).forEach(([key, value]) => {
              if (value !== 0) {
                //@ts-ignore
                result[key.toLocaleUpperCase()] = {
                  total: value,
                  discount: 0,
                  payable: value, // since discount is 0
                  remarks: "",
                };
              }
            });
          });

          console.log("******* uniqueNonZeroKeys *********");
          console.log(result);
          setDiscountData(result);
          setSelectedStudent(rowData.student_id);
        }
      }

      setFetchingFeeStructure(false);
    } catch (error) {
      console.log(error);
      setFetchingFeeStructure(false);
      snackbarRef.current?.showSnackbar(`Some Error Occured`, "error");
    }
  };

  // Define columns with DataGrid
  const columns: GridColDef[] = [
    {
      field: "admission_id",
      headerName: "Admission ID",
      flex: 1,
      valueGetter: (_, row) => {
        return row.admission_id.toString().padStart(5, "0");
      },
    },
    {
      field: "personal_details",
      headerName: "Name",
      flex: 1,
      valueGetter: (_, row) => row.personal_details.name,
    },
    {
      field: "parent_details",
      headerName: "Father's Name",
      flex: 1,
      valueGetter: (_, row) => row.father_details.name,
    },
    {
      field: "class",
      headerName: "Class",
      flex: 1,
      valueGetter: (_, row) => {
        return `${row.class_name} (${row.section_name})`;
      },
    },
    {
      field: "roll_number",
      headerName: "Roll No.",
      flex: 0.5,
      valueGetter: (_, row) => row.roll_number,
      // valueGetter: (_, row) => row.academic_records[0]?.roll_number,
    },

    {
      field: "parent_contact",
      headerName: "Contact",
      flex: 1,
      valueGetter: (_, row) =>
        row.father_details.contact ||
        row.mother_details.contact ||
        row.guardian_details.contact,
    },
    // {
    //   field: "is_active",
    //   headerName: "Status",
    //   flex: 0.8,
    //   renderCell: (params) => (
    //     <>
    //       <Box
    //         sx={{
    //           display: "flex",
    //           alignItems: "center",
    //           justifyContent: "center",
    //           flexDirection: "row",
    //           gap: 1,
    //           backgroundColor:
    //             params.row.is_active === "active" ? "#00c9a6" : "lightgray",
    //           borderRadius: 4,
    //           padding: "4px 8px",
    //           marginTop: 0.6,
    //           //   marginTop: 1,
    //         }}
    //       >
    //         <Box
    //           display={"flex"}
    //           sx={{
    //             width: 10,
    //             height: 10,
    //             borderRadius: "50%",
    //             backgroundColor:
    //               params.row.is_active === "active" ? "green" : "gray",
    //           }}
    //         />
    //         <Typography variant="body2">
    //           {params.row.is_active === "active" ? "Active" : "In-Active"}
    //         </Typography>
    //       </Box>
    //     </>
    //   ),
    // },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <>
          <AnimatedButton
            label="Discounts"
            onClick={() => {
              handleViewClick(params.row);
            }}
            disabled={false}
          />
        </>
      ),
    },
  ];

  const onResetHandler = () => {
    reset({
      admission_id: "",
      name: "",
      class_id: "",
      section_id: "",
      roll_number: "",
    });
    setApplications([]);
  };

  const HandleFetchStudents = async (data: any) => {
    try {
      console.log("Go Clicked for Class");
      console.log(data);
      setFetching(true);
      setApplications([]);
      const { admission_id, class_id, section_id, roll_number, name } = data;

      const isAdmissionIdSearch = admission_id?.trim() || name?.trim();
      const isClassBasedSearch = class_id && section_id;
      if (!isAdmissionIdSearch && !isClassBasedSearch) {
        setError("admission_id", {
          type: "manual",
          message: "Required (or use Student Name or Class and Section)",
        });

        setError("name", {
          type: "manual",
          message: "Required (or use Admission Id or Class and Section)",
        });

        setError("class_id", {
          type: "manual",
          message:
            "Required with Section (or use Student Name Or Admission ID)",
        });

        setError("section_id", {
          type: "manual",
          message: "Required with Class (or use Student Name Or Admission ID)",
        });

        return;
      }

      const payload = {
        admission_id,
        class_id,
        section_id,
        roll_number: parseInt(roll_number),
        name,
      };
      console.log(payload);
      const students_records = await ListFilteredStudents(payload);
      if (students_records && students_records.result.documents?.length > 0) {
        const student_string = students_records.result.documents;
        const student_json_parsed = JSONParseStudentObject(student_string);

        setApplications(student_json_parsed);
      } else {
        snackbarRef.current?.showSnackbar(`No Record Found`, "info");
      }
    } catch (error) {
      console.log(error);
      snackbarRef.current?.showSnackbar(`Some Error occured`, "error");
    } finally {
      setFetching(false);
    }
  };

  const handleDiscountChange = (key: any, value: any) => {
    const discount = parseInt(value) || 0;
    setDiscountData((prev: any) => ({
      ...prev,
      [key]: {
        ...prev[key],
        discount,
        payable: Math.max(0, prev[key].total - discount),
      },
    }));
  };

  const handleGivenByChange = (key: any, value: any) => {
    setDiscountData((prev: any) => ({
      ...prev,
      [key]: {
        ...prev[key],
        givenBy: value,
      },
    }));
  };

  const handleSave = async () => {
    try {
      if (!selectedStudent.student_id) {
        snackbarRef.current?.showSnackbar(`Student Not Selected`, "error");
        return;
      }
      setUpdatingDiscount(true);
      console.log("Saving data:", discountData);
      const payload = {
        user: loggedInUser || "testuser",
        arrayOfItems: [
          {
            id: selectedStudent.student_id,
            discount: JSON.stringify(discountData),
          },
        ],
      };
      console.log(payload);

      const response = await UpdateStudentv2(payload);
      if (response) {
        if (response.status === "SUCCESS") {
          setApplications((prev: any) =>
            prev.map((student: any) =>
              student.student_id === selectedStudent.student_id
                ? { ...student, discount: JSON.stringify(discountData) }
                : student
            )
          );

          snackbarRef.current?.showSnackbar(`${response.message}`, "success");
        }

        if (response.status === "FAIL")
          snackbarRef.current?.showSnackbar(`${response.message}`, "warning");
      } else {
        snackbarRef.current?.showSnackbar(`Some Error. Not Updated`, "warning");
      }
    } catch (error) {
      console.log(error);
      snackbarRef.current?.showSnackbar(`Some Error Occured`, "error");
    } finally {
      setUpdatingDiscount(false);
    }
  };

  const handleClear = () => {
    // setFees(initialFees);
  };

  return (
    <>
      <ToastSnackbar ref={snackbarRef} />

      <Box
        display={"flex"}
        flexDirection={"column"}
        p={2}
        height="auto"
        // style={{ minHeight: "100vh" }}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"space-between"}
        >
          <Box
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"flex-start"}
            p={2}
            pb={0}
            gap={1}
          >
            <MyCustomButton
              variant="contained"
              startIcon={<ArrowLeftIcon />}
              onClick={() => navigate("..")}
            >
              Go Back
            </MyCustomButton>
            <MyCustomButton
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={() => navigate("/home")}
            >
              Home
            </MyCustomButton>
          </Box>
        </Box>

        {/**This is Datagrid */}
        <Grid container direction="column" mt={4}>
          <Typography variant="h5" alignSelf={"center"} mb={1}>
            <strong>View Add Or Edit Fee heads Discounts</strong>
          </Typography>
          <Grid item xs={12}>
            <Box
              display={"flex"}
              flexDirection={"column"}
              p={2}
              pt={0}
              height="auto"
              justifyContent={"center"}
              alignItems={"center"}
              mt={1}
            >
              <form
                onSubmit={handleSubmit(HandleFetchStudents)}
                onReset={onResetHandler}
              >
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  justifyContent={"space-evenly"}
                  gap={2}
                  width="90vw"
                >
                  <ControlledTextField
                    name="admission_id"
                    control={control}
                    errors={errors}
                    label="Admission Id"
                    sx={{ width: "30%", mt: 0 }}
                  />
                  <ControlledTextField
                    name="name"
                    control={control}
                    errors={errors}
                    label="Student Name"
                    sx={{ width: "30%", mt: 0 }}
                  />
                  <ControlledSelect
                    name={`class_id`}
                    control={control}
                    errors={errors}
                    label="Class"
                    options={classList.map((item: any) => ({
                      value: item.class_id,
                      label: item.name,
                    }))}
                    sx={{ width: "30%" }}
                  />
                  <ControlledSelect
                    name={`section_id`}
                    control={control}
                    errors={errors}
                    label="Section"
                    options={sectionList.map((item: any) => ({
                      value: item.section_id,
                      label: item.name,
                    }))}
                    sx={{ width: "30%" }}
                  />
                  {/* <ControlledTextField
                      name={"roll_number"}
                      control={control}
                      errors={errors}
                      label="Roll Number"
                      type="number"
                      sx={{ width: "30%", mt: 0 }}
                    /> */}

                  <MyCustomButton
                    variant="contained"
                    type="submit"
                    startIcon={fetching ? <CircularProgress size={20} /> : null}
                    disabled={fetching}
                    sx={{
                      alignSelf: "center",
                      height: "70%",
                      width: "30%",
                    }}
                  >
                    {fetching ? "" : "Fetch Students"}
                  </MyCustomButton>
                  <MyCustomButton
                    variant="contained"
                    type="reset"
                    sx={{
                      alignSelf: "center",
                      height: "70%",
                      width: "30%",
                    }}
                  >
                    Clear
                  </MyCustomButton>
                </Box>
              </form>
            </Box>

            <DataGrid
              rows={applications}
              columns={columns}
              rowHeight={40}
              getRowId={(row) => row.student_id}
              pageSizeOptions={[50, 100]}
              loading={loading}
              // autoHeight // Prevents unnecessary re-renders
              rowBufferPx={300} // Loads extra rows for smoother scrolling
              disableColumnFilter // Improves performance
              disableColumnMenu // Removes menu for better speed
              disableRowSelectionOnClick // Prevents unnecessary re-renders
              slots={{
                toolbar: GridToolbar,
                noRowsOverlay: () => <CustomNoRowsOverlay loading={loading} />,
              }}
              slotProps={{ toolbar: { showQuickFilter: true } }}
              sx={{
                maxWidth: "98vw",
                height: "65vh", // Fixed height for the grid
                // Target the column headers specifically

                "& .MuiDataGrid-row:hover": {
                  transform: "scale(1)", // Hover zoom effect
                  backgroundColor: "#f5f5f5", // Light background on hover
                  "& .MuiDataGrid-cell": {
                    color: "#2E186A", // Change text color on hover
                    fontWeight: "bold", // Bold text on hover
                  },
                },
                "& .MuiDataGrid-row.Mui-selected": {
                  backgroundColor: "#f0f0f0", // Active row background color
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#1e88e5", // Custom background color for header
                  // fontFamily: "Motiva Sans Bold",
                  color: "#2e186a", // Custom text color for header
                  fontSize: "1rem", // Increase font size of header
                  borderBottom: "2px solid #fff", // Add a border at the bottom of headers
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  textOverflow: "clip", // Prevent ellipsis from appearing
                  whiteSpace: "normal", // Ensure text wraps if it's too long
                  lineHeight: "1", // Adjust line height
                },
                "& .MuiDataGrid-columnHeader": {
                  padding: "0px 10px", // Add padding to header cells
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {isProfileDialogOpen && (
        <Dialog
          open={isProfileDialogOpen}
          onClose={onClose}
          maxWidth={false}
          sx={{
            display: "flex",
            alignItems: "center", // ✅ Center vertically
            justifyContent: "center", // ✅ Center horizontally
            "& .MuiDialog-paper": {
              width: "95vw", // Set dialog width
              height: "50vw",
              maxWidth: "95vw",
              minHeight: "20vh", // Prevents it from being too short
            },
          }}
        >
          <DialogTitle>
            <Typography variant="h5">
              <strong>{`Discount Configuration For ${
                selectedStudent.personal_details.name
              }-${selectedStudent.class_name} (${
                selectedStudent.section_name
              }) Roll ${selectedStudent?.roll_number || "NA"}`}</strong>
            </Typography>

            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <GridCloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              // overflowX: "auto",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "8px", // Width of the scrollbar
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1", // Background of the scrollbar track
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888", // Color of the scroll thumb
                borderRadius: "10px", // Rounded corners
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#555", // Darker color on hover for the thumb
              },
            }}
          >
            {fetchingFeeStructure ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="200px"
              >
                <CircularProgress />
              </Box>
            ) : (
              <Paper
                sx={{ p: 2, position: "relative", marginTop: 1 }}
                elevation={4}
              >
                <Grid container spacing={0}>
                  <Grid item xs={10}>
                    {Object.entries(discountData).map(([feeHead, data]) => (
                      <Grid
                        container
                        spacing={1}
                        alignItems="center"
                        key={feeHead}
                        sx={{ mb: 2 }}
                      >
                        <Grid item xs={2}>
                          <Typography variant="body2" fontWeight="bold">
                            {feeHead.toLocaleUpperCase()}
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <TextField
                            label="Total (₹)"
                            //@ts-ignore
                            value={data.total}
                            InputProps={{ readOnly: true }}
                            size="small"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <TextField
                            label="Discount (₹)"
                            type="number"
                            //@ts-ignore
                            value={data.discount === 0 ? "" : data.discount}
                            onKeyDown={(e) => {
                              if (e.key === "-" || e.key === "e") {
                                e.preventDefault();
                              }
                            }}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => {
                              let value = Number(e.target.value);
                              // Clamp to valid range
                              if (value < 0) value = 0;
                              //@ts-ignore
                              if (value > Number(data.total)) {
                                //@ts-ignore
                                value = data.total;
                              }

                              handleDiscountChange(feeHead, value);
                            }}
                            size="small"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <TextField
                            label="Payable (₹)"
                            //@ts-ignore
                            value={data.payable}
                            InputProps={{ readOnly: true }}
                            size="small"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            label="Given By"
                            select
                            //@ts-ignore
                            value={data.givenBy}
                            onChange={(e) =>
                              handleGivenByChange(feeHead, e.target.value)
                            }
                            size="small"
                            fullWidth
                          >
                            {givenByOptions.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Buttons vertically aligned on the right */}
                  <Grid item xs={2}>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <MyCustomButton
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleSave}
                        disabled={updatingDiscount}
                        startIcon={
                          updatingDiscount ? (
                            <CircularProgress size={20} />
                          ) : undefined
                        }
                      >
                        {updatingDiscount ? "..Wait" : "Save"}
                      </MyCustomButton>
                      <MyCustomButton
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={onClose}
                      >
                        Close
                      </MyCustomButton>
                      <Typography variant="caption" color="textSecondary">
                        <strong>
                          NOTE: Given discounts will be adjusted for each month
                          during fee payment (Fee Collection).
                        </strong>
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default React.memo(StudentScholarship);
